import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { Calculator, Search, Menu, X, Clock } from 'lucide-react';
import { CATEGORIES, getAllTools } from '../data/calculators';
import { CALC_REGISTRY } from '../calculators/registry';

const RECENT_KEY = 'calc-recent-tools';

// Cheap fuzzy score: sum of character position bonuses; higher is better.
function fuzzyScore(name, q) {
  const n = name.toLowerCase(); const s = q.toLowerCase();
  if (!s) return 0;
  if (n === s) return 10000;
  if (n.startsWith(s)) return 5000 - n.length;
  const idx = n.indexOf(s);
  if (idx >= 0) return 2000 - idx * 10 - n.length;
  // subsequence match
  let ni = 0, score = 800, gap = 0;
  for (const ch of s) {
    const k = n.indexOf(ch, ni);
    if (k < 0) return -1;
    gap += k - ni; ni = k + 1;
  }
  return score - gap * 2;
}

export default function Layout() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [recent, setRecent] = useState([]);
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const tools = getAllTools();

  useEffect(() => {
    try { const r = localStorage.getItem(RECENT_KEY); if (r) setRecent(JSON.parse(r)); } catch {}
  }, []);

  const pushRecent = (slug, name) => {
    const next = [{ slug, name }, ...recent.filter(x => x.slug !== slug)].slice(0, 6);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
  };

  const suggestions = q.length > 0
    ? tools
        .map(t => ({ ...t, score: fuzzyScore(t.name, q) }))
        .filter(t => t.score >= 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8)
    : [];

  // Reset active index whenever query changes
  useEffect(() => { setActiveIdx(0); }, [q]);

  // Close suggestion box on outside click
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setQ(''); setFocused(false); } };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const jump = (s) => { pushRecent(s.slug, s.name); navigate(`/calc/${s.slug}`); setQ(''); setFocused(false); inputRef.current?.blur(); };

  const onKey = (e) => {
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((activeIdx + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((activeIdx - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Enter') { e.preventDefault(); const s = suggestions[activeIdx]; if (s) jump(s); }
    else if (e.key === 'Escape') { setQ(''); setFocused(false); inputRef.current?.blur(); }
  };

  const showRecent = focused && !q && recent.length > 0;
  const showDropdown = focused && (suggestions.length > 0 || showRecent);

  return (
    <div>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="header-home">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900">PocketCalculator.net</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {CATEGORIES.map(cat => (
              <Link key={cat.id} to={`/category/${cat.id}`} className="text-sm font-medium text-slate-700 hover:text-blue-600 transition-colors">
                {cat.name.replace(' Calculators', '')}
              </Link>
            ))}
          </nav>

          <div className="relative hidden sm:block" ref={boxRef}>
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              onFocus={() => { setActiveIdx(0); setFocused(true); }}
              onKeyDown={onKey}
              placeholder="Search calculators… (↑↓ ⏎)"
              className="pl-9 pr-3 py-2 text-sm bg-slate-100 rounded-full w-72 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 border border-transparent focus:border-blue-500"
              data-testid="header-search"
            />
            {showDropdown && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-lg border border-slate-200 py-2 max-h-96 overflow-y-auto z-50" data-testid="search-suggestions">
                {showRecent && (
                  <>
                    <div className="px-4 py-1 text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Recent</div>
                    {recent.map(s => (
                      <button key={s.slug} onClick={() => jump(s)} className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-slate-700">
                        {s.name}
                      </button>
                    ))}
                    {suggestions.length > 0 && <div className="border-t my-1"/>}
                  </>
                )}
                {suggestions.map((s, i) => (
                  <button
                    key={s.slug}
                    onClick={() => jump(s)}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={`w-full text-left px-4 py-2 text-sm ${i === activeIdx ? 'bg-blue-50 text-blue-900' : 'text-slate-700 hover:bg-blue-50'}`}
                  >
                    {s.name}
                    {!CALC_REGISTRY[s.slug] && <span className="ml-2 text-xs text-orange-500">(coming soon)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="md:hidden" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden bg-white border-t border-slate-200 py-3 px-4 space-y-2">
            {CATEGORIES.map(cat => (
              <Link key={cat.id} to={`/category/${cat.id}`} onClick={() => setOpen(false)} className="block py-2 text-sm font-medium text-slate-700">
                {cat.name}
              </Link>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-bold text-slate-900 mb-3">PocketCalculator.net</div>
            <div className="text-slate-600 leading-6">Free, fast, no-signup calculators for math, finance, fitness &amp; daily life.</div>
          </div>
          <div>
            <div className="font-bold text-slate-900 mb-3">Popular Tools</div>
            <ul className="space-y-1.5">
              <li><Link className="text-slate-600 hover:text-blue-700" to="/calc/mortgage">Mortgage Calculator</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/calc/bmi">BMI Calculator</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/calc/percentage">Percentage Calculator</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/calc/fraction">Fraction Calculator</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/calc/scientific">Scientific Calculator</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/calc/loan">Loan Calculator</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-bold text-slate-900 mb-3">Categories</div>
            <ul className="space-y-1.5">
              {CATEGORIES.map(cat => (
                <li key={cat.id}><Link className="text-slate-600 hover:text-blue-700" to={`/category/${cat.id}`}>{cat.name}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-bold text-slate-900 mb-3">Company</div>
            <ul className="space-y-1.5">
              <li><Link className="text-slate-600 hover:text-blue-700" to="/blog">Blog</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/about">About Us</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/contact">Contact Us</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/terms">Terms &amp; Conditions</Link></li>
              <li><Link className="text-slate-600 hover:text-blue-700" to="/privacy">Privacy Policy</Link></li>
              <li><a className="text-slate-600 hover:text-blue-700" href="mailto:saurabhdn99@gmail.com">saurabhdn99@gmail.com</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-5 text-center text-sm text-slate-500">
          &copy; {new Date().getFullYear()} PocketCalculator.net — All rights reserved.
        </div>
      </footer>
    </div>
  );
}
